import React, { memo } from 'react';
import classNames from 'classnames';
import { useIntl } from 'umi';
import { MainContentCmp, AgentVegaChartAsync } from '@/pages/aigc/pages/Agent/ChatPage/components/SubComp';
import AgentThinkTool from '@/pages/aigc/assets/svg/agent-think-tool-use.svg';
import { StreamingText } from '@/pages/aigc/components/StreamingText';
import type { ISwitchAgentAccordionItem } from '../index';

interface IThinkAccordionItemRendererProps {
  item: ISwitchAgentAccordionItem;
  streamingState?: string;
  messageId?: string;
}

const ThinkAccordionItemRendererInner = ({
  item,
  streamingState,
  messageId,
}: IThinkAccordionItemRendererProps) => {
  const { formatMessage } = useIntl();

  if (item.type === 'switch_agent') {
    const agentText = `${item.new_agent || ''}${item.input ? `: ${item.input}` : ''}`;
    return (
      <MainContentCmp
        value={agentText}
        streamingState={streamingState}
        from="think"
        messageId={messageId}
      />
    );
  }

  if (
    item.type === 'think' ||
    item.type === 'think_tool' ||
    item.type === 'text'
  ) {
    return (
      <div
        className={classNames({
          'dba-text-tool-item': item.type === 'text',
        })}
      >
        <MainContentCmp
          value={item.value}
          streamingState={streamingState}
          from="think"
          messageId={messageId}
          className={
            item.type === 'text'
              ? 'dba-text-tool-markdown'
              : item.type === 'think_tool'
                ? 'dba-tool-use-collapse-markdown'
                : undefined
          }
          isShowReference={item.type === 'think_tool' ? false : undefined}
        />
      </div>
    );
  }

  if (item.type === 'bq_chart') {
    return (
      <AgentVegaChartAsync
        value={item.value}
        messageId={messageId}
        from="think"
      />
    );
  }

  if (item.type === 'tool_use') {
    return (
      <span className="dba-system-message-input dba-system-message-input-tool-use">
        <img src={AgentThinkTool} alt="" className="dba-tool-use-icon" />
        {`${formatMessage({ id: 'dba_switch_tool' })}${item.value}`}
      </span>
    );
  }

  if (item.type === 'streaming') {
    return (
      <StreamingText
        textColor="#838697"
        streamColor="#000000"
        fontSize="14px"
        duration={1.5}
        loop={true}
        autoPlay={true}
        className="dba-streaming-text--think"
      />
    );
  }

  if (item.type === 'error') {
    return (
      <span className="dba-system-message-input dba-system-message-input-tool-use">
        {formatMessage({ id: 'dba_chart_stop' })}
      </span>
    );
  }

  if (item.value) {
    return (
      <span className="dba-system-message-input dba-system-message-input-tool-use">
        {item.value}
      </span>
    );
  }

  return null;
};

const isSameRenderItem = (
  prev: IThinkAccordionItemRendererProps,
  next: IThinkAccordionItemRendererProps,
): boolean =>
  prev.streamingState === next.streamingState &&
  prev.messageId === next.messageId &&
  prev.item.type === next.item.type &&
  prev.item.thinkId === next.item.thinkId &&
  prev.item.tool_call_id === next.item.tool_call_id &&
  prev.item.value === next.item.value &&
  prev.item.new_agent === next.item.new_agent &&
  prev.item.input === next.item.input;

export const ThinkAccordionItemRenderer = memo(
  ThinkAccordionItemRendererInner,
  isSameRenderItem,
);
ThinkAccordionItemRenderer.displayName = 'ThinkAccordionItemRenderer';

interface IThinkInlineListProps {
  items: ISwitchAgentAccordionItem[];
  streamingState?: string;
  messageId?: string;
  keyPrefix: string;
}

export const ThinkInlineList = memo(({
  items,
  streamingState,
  messageId,
  keyPrefix,
}: IThinkInlineListProps) => (
  <>
    {items.map((item, index) => (
      <ThinkAccordionItemRenderer
        key={`${keyPrefix}-${item.thinkId || index}-${item.type}`}
        item={item}
        streamingState={streamingState}
        messageId={messageId}
      />
    ))}
  </>
), (prev, next) => {
  if (
    prev.streamingState !== next.streamingState ||
    prev.messageId !== next.messageId ||
    prev.keyPrefix !== next.keyPrefix ||
    prev.items.length !== next.items.length
  ) {
    return false;
  }
  return prev.items.every((item, index) => {
    const nextItem = next.items[index];
    return (
      item.type === nextItem.type &&
      item.thinkId === nextItem.thinkId &&
      item.value === nextItem.value &&
      item.new_agent === nextItem.new_agent &&
      item.input === nextItem.input
    );
  });
});
ThinkInlineList.displayName = 'ThinkInlineList';
